import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, CheckCircle, Loader2, AlertCircle, Users, Calendar, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import api from '../utils/api';

const SelfCheckIn = () => {
  const navigate = useNavigate();
  
  const [bookingId, setBookingId] = useState('');
  const [lastName, setLastName] = useState('');
  const [searching, setSearching] = useState(false);
  const [checkingIn, setCheckingIn] = useState(false);
  const [booking, setBooking] = useState(null);
  const [checkedIn, setCheckedIn] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    
    if (!bookingId.trim() || !lastName.trim()) {
      toast.error('Please enter both Booking ID and Last Name');
      return;
    }

    setSearching(true);
    try {
      // Use v2 PostgreSQL endpoint
      const response = await api.post('/v2/public/self-check-in/lookup', {
        confirmationCode: bookingId.trim().toUpperCase(),
        lastName: lastName.trim()
      });
      
      if (response.data.found) {
        setBooking(response.data.booking);
        toast.success('Booking found!');
      } else {
        toast.error('Booking not found. Please check your details.');
        setBooking(null);
      }
    } catch (error) {
      console.error('Lookup error:', error);
      const errorMsg = error.response?.data?.detail || 'Booking not found';
      toast.error(errorMsg);
      setBooking(null);
    } finally {
      setSearching(false);
    }
  };

  const handleCheckIn = async () => {
    setCheckingIn(true);
    try {
      // Use v2 PostgreSQL endpoint
      const response = await api.post(`/v2/public/self-check-in/${booking.id}`);
      
      if (response.data.alreadyCheckedIn) {
        toast.info('You are already checked in!');
      } else {
        toast.success('✅ Checked in successfully!');
      }
      
      setCheckedIn(true);
      setBooking({ ...booking, checkInStatus: 'checked_in', checkInAt: response.data.checkInAt });
    } catch (error) {
      console.error('Check-in error:', error);
      toast.error(error.response?.data?.detail || 'Check-in failed');
    } finally {
      setCheckingIn(false);
    }
  };

  const resetForm = () => {
    setBookingId('');
    setLastName('');
    setBooking(null);
    setCheckedIn(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4 pb-16">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-full mb-4">
            <CheckCircle className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Self Check-In</h1>
          <p className="text-sm sm:text-base text-gray-600">Ready for your booking? Check in now</p>
        </div>

        {/* Main Card - using standard shadow, no border */}
        <div className="bg-white rounded-xl shadow-[0_8px_24px_rgba(0,0,0,0.04)] p-6 sm:p-8">
          {!booking && !checkedIn && (
            <form onSubmit={handleSearch} className="space-y-5">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Booking ID <span className="text-red-500">*</span>
                </label>
                <Input
                  type="text"
                  value={bookingId}
                  onChange={(e) => setBookingId(e.target.value.toUpperCase())}
                  placeholder="e.g., BF-ABC123"
                  className="h-11 text-base font-mono"
                  disabled={searching}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Last Name <span className="text-red-500">*</span>
                </label>
                <Input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Enter your last name"
                  className="h-11 text-base"
                  disabled={searching}
                />
              </div>

              <Button
                type="submit"
                disabled={searching || !bookingId.trim() || !lastName.trim()}
                className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white text-base font-semibold"
              >
                {searching ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Searching...
                  </>
                ) : (
                  <>
                    <Search className="w-5 h-5 mr-2" />
                    Find My Booking
                  </>
                )}
              </Button>
            </form>
          )}

          {booking && !checkedIn && (
            <div className="space-y-5">
              <div className="text-center pb-4 border-b border-gray-100">
                <CheckCircle className="w-10 h-10 text-green-600 mx-auto mb-2" />
                <h2 className="text-lg sm:text-xl font-bold text-gray-900">Booking Found!</h2>
              </div>

              <div className="space-y-3">
                <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Customer</p>
                  <p className="text-base sm:text-lg font-bold text-gray-900">{booking.customerName}</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Service</p>
                  <p className="text-sm sm:text-base font-semibold text-gray-900">{booking.serviceName}</p>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Date</p>
                    <div className="flex items-center gap-1.5">
                      <Calendar className="w-3.5 h-3.5 text-gray-500" />
                      <p className="text-xs sm:text-sm font-semibold text-gray-900">
                        {(booking.preferredDate || booking.date) 
                          ? new Date(booking.preferredDate || booking.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                          : 'N/A'}
                      </p>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Time</p>
                    <div className="flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5 text-gray-500" />
                      <p className="text-xs sm:text-sm font-semibold text-gray-900">
                        {booking.preferredTime || booking.startTime || 'N/A'}
                        {booking.endTime && ` - ${booking.endTime}`}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Party Size & Duration Row */}
                <div className="grid grid-cols-2 gap-3">
                  {(booking.partySize || booking.groupSize) && (
                    <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Party Size</p>
                      <div className="flex items-center gap-1.5">
                        <Users className="w-3.5 h-3.5 text-gray-500" />
                        <p className="text-sm sm:text-base font-semibold text-gray-900">{booking.partySize || booking.groupSize} guests</p>
                      </div>
                    </div>
                  )}
                  
                  {booking.duration && (
                    <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Duration</p>
                      <div className="flex items-center gap-1.5">
                        <Clock className="w-3.5 h-3.5 text-gray-500" />
                        <p className="text-sm sm:text-base font-semibold text-gray-900">
                          {booking.duration >= 60 ? `${Math.floor(booking.duration / 60)}h ${booking.duration % 60 > 0 ? `${booking.duration % 60}m` : ''}` : `${booking.duration}min`}
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Timezone */}
                {booking.timezone && (
                  <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Timezone</p>
                    <p className="text-xs sm:text-sm font-semibold text-gray-900">{booking.timezone}</p>
                  </div>
                )}

                {/* Add-ons */}
                {(booking.selectedAddOns?.length > 0 || booking.addOns?.length > 0) && (
                  <div className="bg-blue-50 rounded-lg p-3 sm:p-4">
                    <p className="text-xs text-blue-600 uppercase tracking-wide mb-2 font-medium">Add-ons</p>
                    <div className="space-y-1">
                      {(booking.selectedAddOns || booking.addOns).map((addon, idx) => (
                        <div key={idx} className="flex justify-between text-sm">
                          <span className="text-gray-700">
                            {addon.title || addon.name}
                            {addon.quantity > 1 && ` (x${addon.quantity})`}
                            {addon.selectedOption && `: ${addon.selectedOption}`}
                          </span>
                          {addon.price > 0 && <span className="text-gray-900 font-medium">${addon.price}</span>}
                        </div>
                      ))}
                    </div>
                    {booking.addOnsTotal > 0 && (
                      <div className="border-t border-blue-100 mt-2 pt-2 flex justify-between text-sm font-semibold">
                        <span className="text-blue-700">Add-ons Total</span>
                        <span className="text-blue-900">${booking.addOnsTotal.toFixed(2)}</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Custom Question Answers */}
                {(booking.customQuestionAnswers?.length > 0 || booking.customAnswers?.length > 0) && (
                  <div className="bg-purple-50 rounded-lg p-3 sm:p-4">
                    <p className="text-xs text-purple-600 uppercase tracking-wide mb-2 font-medium">Additional Information</p>
                    <div className="space-y-2">
                      {(booking.customQuestionAnswers || booking.customAnswers).map((qa, idx) => (
                        <div key={idx} className="text-sm">
                          <p className="text-gray-500 text-xs">{qa.question}</p>
                          <p className="text-gray-900 font-medium">{qa.answer || qa.value || '-'}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className={`rounded-lg p-3 sm:p-4 ${
                  booking.checkInStatus === 'checked_in' 
                    ? 'bg-green-50' 
                    : 'bg-amber-50'
                }`}>
                  <p className={`text-xs uppercase tracking-wide mb-1 ${
                    booking.checkInStatus === 'checked_in' ? 'text-green-600' : 'text-amber-600'
                  }`}>Status</p>
                  <p className={`text-sm sm:text-base font-bold ${
                    booking.checkInStatus === 'checked_in' ? 'text-green-700' : 'text-amber-700'
                  }`}>
                    {booking.checkInStatus === 'checked_in' ? '✅ Already Checked In' : '⏳ Ready to Check In'}
                  </p>
                </div>
              </div>

              {booking.checkInStatus !== 'checked_in' ? (
                <Button
                  onClick={handleCheckIn}
                  disabled={checkingIn}
                  className="w-full h-11 bg-green-600 hover:bg-green-700 text-white text-base font-bold"
                >
                  {checkingIn ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Checking In...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-5 h-5 mr-2" />
                      Check In Now
                    </>
                  )}
                </Button>
              ) : (
                <div className="bg-green-50 rounded-lg p-4 text-center">
                  <p className="text-green-700 font-semibold text-sm sm:text-base">You're already checked in! See you soon 👋</p>
                </div>
              )}

              <Button
                onClick={resetForm}
                variant="outline"
                className="w-full h-10 border-gray-200 hover:bg-gray-50"
              >
                Back to Search
              </Button>
            </div>
          )}

          {checkedIn && (
            <div className="text-center space-y-5">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-2">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              
              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">You're Checked In! 🎉</h2>
                <p className="text-base sm:text-lg font-semibold text-gray-700">{booking.customerName}</p>
                <p className="text-xs sm:text-sm text-gray-500 mt-2">
                  Checked in at {new Date(booking.checkInAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>

              <div className="bg-green-50 rounded-lg p-5">
                <p className="text-green-700 font-medium text-sm sm:text-base">See you soon! 👋</p>
              </div>

              <Button
                onClick={resetForm}
                variant="outline"
                className="w-full h-10 border-gray-200 hover:bg-gray-50"
              >
                Check In Another Booking
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SelfCheckIn;
